import { defaultStorefront, storefrontErrors, type FrontStyle, type Storefront } from './storefront';

/**
 * How a restaurant appears in FirstMango Town. The business type decides where it sits and which looks it can
 * have: a shop is a building on the street; a street stall is a cart on the pavement; market stalls cluster in
 * their market's lane (lit and busy only on market days); a food truck parks at the kerb.
 */
export type BusinessType = 'shop' | 'street' | 'market' | 'truck';

export const BUSINESS: Record<BusinessType, { th: string; en: string; hint: string; styles: FrontStyle[] }> = {
  shop: { th: 'ร้านอาหาร', en: 'Shop', hint: 'ร้านในอาคาร · A restaurant, café or shophouse', styles: ['shophouse', 'cafe', 'wooden', 'modern'] },
  street: { th: 'ร้านริมทาง', en: 'Street stall', hint: 'รถเข็น แผงลอย · A cart or stall on the pavement', styles: ['stall'] },
  market: { th: 'ร้านในตลาด', en: 'Market stall', hint: 'ตลาดนัด ตลาดกลางคืน ถนนคนเดิน · Night market, walking street', styles: ['tent'] },
  truck: { th: 'ฟู้ดทรัค', en: 'Food truck', hint: 'จอดขายตามที่ · Parks and sells', styles: ['truck'] },
};

/** Market days (0 = Sunday … 6 = Saturday) and hours, Bangkok time. close < open means past midnight. */
export interface MarketHours { days: number[]; open: string; close: string }

export interface TownProfile {
  type: BusinessType;
  front: Storefront;
  /** Where the shop really is (for distance and delivery); null = not set yet. */
  location: { lat: number; lng: number } | null;
  /** Own delivery radius in metres (0 = no delivery). */
  deliveryM: number;
  /** Shown in FirstMango Town (needs a location). */
  listed: boolean;
  /** Market stalls: which market (stalls with the same name cluster together) and when it runs. */
  market?: { name: string; hours: MarketHours };
}

export const DELIVERY_MAX_M = 15_000;
export const MARKET_NAME_MAX = 32;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function defaultProfile(name: string): TownProfile {
  return { type: 'shop', front: defaultStorefront(name), location: null, deliveryM: 0, listed: false };
}

/** Switching type keeps what still fits and picks that type's look (the cart / tent / truck). */
export function withType(p: TownProfile, type: BusinessType): TownProfile {
  const styles = BUSINESS[type].styles;
  const front: Storefront = styles.includes(p.front.style) ? p.front : { ...p.front, style: styles[0], lots: type === 'shop' ? p.front.lots : 1 };
  return {
    ...p, type, front,
    market: type === 'market' ? p.market ?? { name: 'ตลาดนัด', hours: { days: [5, 6], open: '17:00', close: '23:00' } } : undefined,
  };
}

/** Problems with an owner's town profile (empty = fine). The API applies the same rules. */
export function townProfileErrors(x: unknown): string[] {
  if (!x || typeof x !== 'object') return ['profile must be an object'];
  const p = x as Record<string, unknown>;
  const e: string[] = [];
  const type = p.type as BusinessType;
  if (!(typeof type === 'string' && type in BUSINESS)) e.push('type is not one of the business types');
  e.push(...storefrontErrors(p.front).map((m) => `front: ${m}`));
  const front = p.front as Storefront | undefined;
  if (type in BUSINESS && front && typeof front === 'object') {
    if (!BUSINESS[type].styles.includes(front.style)) e.push(`front: style ${String(front.style)} does not fit a ${type}`);
    if (type !== 'shop' && front.lots !== 1) e.push('front: stalls, tents and trucks are 1 lot wide');
  }
  const loc = p.location as { lat?: unknown; lng?: unknown } | null;
  if (loc !== null && !(loc && typeof loc === 'object' && typeof loc.lat === 'number' && typeof loc.lng === 'number' && Math.abs(loc.lat) <= 90 && Math.abs(loc.lng) <= 180)) {
    e.push('location must be {lat, lng} or null');
  }
  if (!(typeof p.deliveryM === 'number' && Number.isInteger(p.deliveryM) && p.deliveryM >= 0 && p.deliveryM <= DELIVERY_MAX_M)) e.push(`deliveryM must be 0-${DELIVERY_MAX_M}`);
  if (typeof p.listed !== 'boolean') e.push('listed must be true or false');
  else if (p.listed && !loc) e.push('a location is needed to be listed in the town');
  if (type === 'market') {
    const m = p.market as { name?: unknown; hours?: { days?: unknown; open?: unknown; close?: unknown } } | undefined;
    if (!m || typeof m.name !== 'string' || !m.name.trim() || Array.from(m.name).length > MARKET_NAME_MAX) e.push(`market.name must be 1-${MARKET_NAME_MAX} characters`);
    const h = m?.hours;
    if (!h || !Array.isArray(h.days) || !h.days.length || h.days.some((d) => !Number.isInteger(d) || (d as number) < 0 || (d as number) > 6) || new Set(h.days).size !== h.days.length) e.push('market.hours.days must be distinct days 0-6');
    if (!h || typeof h.open !== 'string' || !HHMM.test(h.open) || typeof h.close !== 'string' || !HHMM.test(h.close) || h.open === h.close) e.push('market.hours open/close must be HH:MM and differ');
  } else if (p.market !== undefined) e.push('market is only for market stalls');
  return e;
}

/** Is the market running at this moment (Bangkok time)? Late-night markets run past midnight into the next day. */
export function marketOpen(h: MarketHours, now: number): boolean {
  const t = new Date(now + 7 * 3600_000);
  const day = t.getUTCDay(), min = t.getUTCHours() * 60 + t.getUTCMinutes();
  const [oh, om] = h.open.split(':').map(Number), [ch, cm] = h.close.split(':').map(Number);
  const o = oh * 60 + om, c = ch * 60 + cm;
  if (o < c) return h.days.includes(day) && min >= o && min < c;
  return (h.days.includes(day) && min >= o) || (h.days.includes((day + 6) % 7) && min < c);
}
